import { AREAS_CRUD_ACTION_CREATORS } from '../actions/areas_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import { ROLE_TYPES } from '../utils/DataConstants';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';

function addIdRecursive(data) {
	return data.map((item) => {
		const newItem = { ...item, id: item.hierarchyName };
		if (newItem.hasChildren && newItem.children.length > 0) {
			newItem.children = addIdRecursive(newItem.children);
		}
		return newItem;
	});
}

// The distinct-areas endpoint resolves each node's hierarchyName through a name-keyed lookup, so leaves
// that share a name under different parents come back with the SAME hierarchyName. That collision makes
// the TreeSelect treat them as one value (selecting either checks both) and leaves the selected-filter
// chips unable to resolve. Rebuild hierarchyName from the actual tree path (root → leaf, joined by ' - '
// to match the backend createHierarchyName / CONCAT_WS format) so every node — and the record it's keyed
// by — has a unique value that still matches the areaHierarchyNames filter.
function rebuildAreaHierarchyNames(data, parentPath = '') {
	return data.map((item) => {
		const name = nullSafeGet('name', item);
		const hierarchyName = parentPath ? `${parentPath} - ${name}` : name;
		const newItem = { ...item, hierarchyName, id: hierarchyName };
		const children = nullSafeGetOrElse('children', newItem, []);
		if (nullSafeGet('hasChildren', newItem) && children.length > 0) {
			newItem.children = rebuildAreaHierarchyNames(children, hierarchyName);
		}
		return newItem;
	});
}

export const areasRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? areasRestCrudThunksForSupplier : areasRestCrudThunksForBuyer;

export const areasRestCrudThunksForBuyer = new RestCrudThunks(
	'areas',
	'area',
	'area',
	'areas',
	AREAS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'location/'
);
export const areasRestCrudThunksForSupplier = new RestCrudThunks(
	'areas',
	'area',
	'area',
	'areas',
	AREAS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'location/'
);

const fetchUtils = new FetchUtils();
export const areasTopLevel =
	(roleType) =>
	(
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) => {
		// if pagination present, transform into limit/offset format and add to query params
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/location/areas/top_level`;
		if (pagination) {
			const paginationQuery = {
				offset: (pagination.current - 1) * pagination.pageSize,
				limit: pagination.pageSize,
			};
			params = {
				...params,
				...paginationQuery,
			};
		}

		// if sorting present, fix order naming and add to query params
		if (sorting) {
			const sortingQuery = { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' };
			params = {
				...params,
				...sortingQuery,
			};
		}

		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(AREAS_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName));

				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							dispatch(AREAS_CRUD_ACTION_CREATORS.fetchError(d.message, d.data));
							reject(d.message);
						} else {
							dispatch(
								AREAS_CRUD_ACTION_CREATORS.fetchSuccess(
									d.data,
									d.count,
									targetCollectionName,
									{
										...pagination,
										total: d.count,
									},
									sorting,
									filters,
									addToTargetCollection
								)
							);
							resolve(d.data);
						}
					})
					.catch((d) => {
						dispatch(AREAS_CRUD_ACTION_CREATORS.fetchError(d.message, d.data));
						reject(d.message);
					});
			});
		};
	};

export const areasTopLevelForBuyer = areasTopLevel('buyer');
export const areasTopLevelForSupplier = areasTopLevel('supplier');

export const fetchDistinctAreas =
	(roleType) =>
	(
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) => {
		// if pagination present, transform into limit/offset format and add to query params
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/location/areas/names/distinct`;
		if (pagination) {
			const paginationQuery = {
				offset: (pagination.current - 1) * pagination.pageSize,
				limit: pagination.pageSize,
			};
			params = {
				...params,
				...paginationQuery,
			};
		}

		// if sorting present, fix order naming and add to query params
		if (sorting) {
			const sortingQuery = { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' };
			params = {
				...params,
				...sortingQuery,
			};
		}

		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(AREAS_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName));

				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							dispatch(AREAS_CRUD_ACTION_CREATORS.fetchError(d.message, d.data));
							reject(d.message);
						} else {
							const parsedData = d.data.map((name) => ({ id: name, name }));
							dispatch(
								AREAS_CRUD_ACTION_CREATORS.fetchSuccess(
									parsedData,
									d.count,
									targetCollectionName,
									{
										...pagination,
										total: d.count,
									},
									sorting,
									filters,
									addToTargetCollection
								)
							);
							resolve(parsedData);
						}
					})
					.catch((d) => {
						dispatch(AREAS_CRUD_ACTION_CREATORS.fetchError(d.message, d.data));
						reject(d.message);
					});
			});
		};
	};

const fetchAreasWithHeirarchyCommonMethod =
	(isDistinct = false) =>
	(roleType) =>
	(
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) => {
		// if pagination present, transform into limit/offset format and add to query params
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${roleType}/location/areas/with_children${
			isDistinct ? '/distinct' : ''
		}`;
		if (pagination) {
			const paginationQuery = {
				offset: (pagination.current - 1) * pagination.pageSize,
				limit: pagination.pageSize,
			};
			params = {
				...params,
				...paginationQuery,
			};
		}

		// if sorting present, fix order naming and add to query params
		if (sorting) {
			const sortingQuery = { ...sorting, order: sorting.order === 'ascend' ? 'asc' : 'desc' };
			params = {
				...params,
				...sortingQuery,
			};
		}

		if (filters) {
			params = {
				...params,
				...filters,
			};
		}

		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(AREAS_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName));

				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							params
						)
					)
					.then(parseJSON)
					.then(({ json, status, ok }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							dispatch(AREAS_CRUD_ACTION_CREATORS.fetchError(d.message, d.data));
							reject(d.message);
						} else {
							// For distinct results, rebuild hierarchy names from the tree path so same-named
							// leaves under different parents stay unique (see rebuildAreaHierarchyNames). Use the
							// rebuilt data for both the stored records and the resolved tree so they stay in sync
							// — otherwise the tree's rebuilt values wouldn't match the records the pills look up.
							const responseData = isDistinct
								? rebuildAreaHierarchyNames(d.data)
								: addIdRecursive(d.data);
							dispatch(
								AREAS_CRUD_ACTION_CREATORS.fetchSuccess(
									responseData,
									d.count,
									targetCollectionName,
									{
										...pagination,
										total: d.count,
									},
									sorting,
									filters,
									addToTargetCollection
								)
							);
							resolve(isDistinct ? responseData : d.data);
						}
					})
					.catch((d) => {
						dispatch(AREAS_CRUD_ACTION_CREATORS.fetchError(d.message, d.data));
						reject(d.message);
					});
			});
		};
	};

export const fetchDistinctAreasHeirarchy = (roleType) =>
	fetchAreasWithHeirarchyCommonMethod(true)(roleType);
export const fetchAreasWithHeirarchy = (roleType) =>
	fetchAreasWithHeirarchyCommonMethod(false)(roleType);
